import type { Question } from '@api/instant_survey/types';
import { Card, IconButton, RadioButtonIcon, RepromptIcon } from '@utilities';

import {
  getHierarchyPromptOptions,
  getQuestionTypeLabel,
  type HierarchyPromptKey,
} from '../../utilities';
import PromptText from './components/PromptText';

import styles from './_index.module.scss';

interface PreviewViewProps {
  hierarchyPromptKeys: HierarchyPromptKey[];
  missingPromptKeys: string[];
  missingPromptMessages: string[];
  onPromptChange: (promptKey: string, value: string) => void;
  onResetPrompts: () => void;
  previewQuestion: Question;
  promptValues: Record<string, string>;
}

const PreviewView = ({
  hierarchyPromptKeys,
  missingPromptKeys,
  missingPromptMessages,
  onPromptChange,
  onResetPrompts,
  previewQuestion,
  promptValues,
}: PreviewViewProps) => {
  const hierarchyPromptOptions = hierarchyPromptKeys.reduce<
    Partial<Record<HierarchyPromptKey, string[]>>
  >((optionsByPromptKey, promptKey) => {
    optionsByPromptKey[promptKey] = getHierarchyPromptOptions(previewQuestion, promptKey).map(
      (option) => option.value
    );
    return optionsByPromptKey;
  }, {});

  return (
    <Card className={styles['preview-view-card']} padding="large">
      <div className={styles['preview-view-card-header']}>
        <span className={styles['preview-view-type']}>
          S1 · {getQuestionTypeLabel(previewQuestion.type).toUpperCase()}
        </span>
        <IconButton
          data-testid="screener-reset-prompts"
          onClick={onResetPrompts}
          tooltip="Reset"
          type="button"
        >
          <RepromptIcon />
        </IconButton>
      </div>
      <div className={styles['preview-view-question']}>
        <PromptText
          hierarchyPromptOptions={hierarchyPromptOptions}
          missingPromptKeys={missingPromptKeys}
          onPromptChange={onPromptChange}
          promptValues={promptValues}
          template={previewQuestion.text}
        />
      </div>
      <div className={styles['preview-view-answers']}>
        {previewQuestion.answers.map(({ id, text }) => (
          <div className={styles['preview-view-answer']} key={id}>
            <RadioButtonIcon />
            <div className={styles['preview-view-answer-text']}>
              <PromptText
                hierarchyPromptOptions={hierarchyPromptOptions}
                missingPromptKeys={missingPromptKeys}
                onPromptChange={onPromptChange}
                promptValues={promptValues}
                template={text}
              />
            </div>
          </div>
        ))}
      </div>
      {missingPromptMessages.length > 0 && (
        <div
          className={styles['preview-view-error']}
          data-testid="screener-missing-prompts-message"
        >
          {missingPromptMessages.map((message) => (
            <p key={message}>{message}</p>
          ))}
        </div>
      )}
    </Card>
  );
};

export default PreviewView;
